/**
 * Frontend preview status for an uploaded payment row (EFT or ACH), shown in the
 * preview dialog before submission. The status names the row's outcome and is the only
 * thing the UI branches on; the row's `error` field carries the human-readable detail
 * behind it (the same role it plays on the persisted KapchargeTransaction.error column)
 * and is display-only.
 *
 * Mirrors LMCreditBackend/utils/payment-processing/transaction-preview-status.js. PaymentNotFound
 * means the bank account matched an active loan but none of the matched loans has a scheduled
 * NLS payment for the row's due date + amount, so the loan the money belongs to is unknown; it
 * is handled exactly like CustomerNotFound.
 * Every status except ManualMatch comes from the backend lookup; ManualMatch is set here
 * after a successful in-place fix.
 *
 * On submit the status is converted to the persisted KapchargeTransactionStatus:
 *   Valid / ManualMatch        => Queued (queued for KapCharge)
 *   HELD_BACK_PREVIEW_STATUSES => Invalid (held back until the data is corrected)
 *   PaidOff                    => not submitted at all (no charge is due)
 */
export enum TransactionPreviewStatus {
	Valid = 'Valid',
	ManualMatch = 'ManualMatch',
	InvalidBranch = 'InvalidBranch',
	CustomerNotFound = 'CustomerNotFound',
	PaymentNotFound = 'PaymentNotFound',
	CustomerInfoIncomplete = 'CustomerInfoIncomplete',
	LookupFailed = 'LookupFailed',
	PaidOff = 'PaidOff',
}

/** Rows submitted but held back as persisted `Invalid`, and highlighted in the preview. */
export const HELD_BACK_PREVIEW_STATUSES: readonly TransactionPreviewStatus[] = [
	TransactionPreviewStatus.InvalidBranch,
	TransactionPreviewStatus.CustomerNotFound,
	TransactionPreviewStatus.PaymentNotFound,
	TransactionPreviewStatus.CustomerInfoIncomplete,
	TransactionPreviewStatus.LookupFailed,
];

export function isHeldBackPreviewStatus(status?: TransactionPreviewStatus | null): boolean {
	return !!status && HELD_BACK_PREVIEW_STATUSES.includes(status);
}

/** Every status except PaidOff is submitted (held-back rows are persisted as Invalid). */
export function isSubmittablePreviewStatus(status?: TransactionPreviewStatus | null): boolean {
	return !!status && status !== TransactionPreviewStatus.PaidOff;
}

/** Badge text. */
export const PREVIEW_STATUS_LABELS: Record<TransactionPreviewStatus, string> = {
	[TransactionPreviewStatus.Valid]: 'Valid',
	[TransactionPreviewStatus.ManualMatch]: 'Manual Match',
	[TransactionPreviewStatus.InvalidBranch]: 'Invalid Branch',
	[TransactionPreviewStatus.CustomerNotFound]: 'Customer Not Found',
	[TransactionPreviewStatus.PaymentNotFound]: 'Payment Not Found',
	[TransactionPreviewStatus.CustomerInfoIncomplete]: 'Incomplete Info',
	[TransactionPreviewStatus.LookupFailed]: 'Lookup Failed',
	[TransactionPreviewStatus.PaidOff]: 'Paid Off',
};

/**
 * Badge colors, drawn from the same palette as KAPCHARGE_STATUS_COLORS so a preview badge
 * and the persisted badge for the same kind of outcome read alike: a row that will be held
 * back carries the warning hue the persisted `Invalid` uses, a genuine fault the error hue.
 */
export const PREVIEW_STATUS_COLORS: Readonly<Record<TransactionPreviewStatus, string>> = {
	[TransactionPreviewStatus.Valid]: '#4caf50', // success-500
	[TransactionPreviewStatus.ManualMatch]: '#ff9800', // warning-500
	[TransactionPreviewStatus.InvalidBranch]: '#f57c00', // warning-700
	[TransactionPreviewStatus.CustomerNotFound]: '#f57c00', // warning-700
	[TransactionPreviewStatus.PaymentNotFound]: '#f57c00', // warning-700
	[TransactionPreviewStatus.CustomerInfoIncomplete]: '#f57c00', // warning-700
	[TransactionPreviewStatus.LookupFailed]: '#d32f2f', // error-700
	[TransactionPreviewStatus.PaidOff]: '#9e9e9e', // neutral
};

/**
 * Resolves the badge color for a status. Bind it to the badge's `--status-color` custom
 * property; the shared `.status-badge` rule derives the pill's tint and text from it.
 */
export function getPreviewStatusColor(status?: TransactionPreviewStatus | null): string {
	return status ? PREVIEW_STATUS_COLORS[status] : PREVIEW_STATUS_COLORS[TransactionPreviewStatus.PaidOff];
}